import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAppContext } from '../../Context';
import { useTranslation } from '../../TranslationContext';

function TermsOverlay() {
	const { psServer, showTerms, setShowTerms, submittedCode, showTermsPopup, setShowTermsPopup } = useAppContext();
	const { t } = useTranslation();
	
	const [questionnaireFields, setQuestionnaireFields] = useState([]);
	const [answers, setAnswers] = useState({});
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [isLoadingQuestionnaire, setIsLoadingQuestionnaire] = useState(true);

	useEffect(() => {
		if (showTermsPopup) {
			const fetchQuestionnaire = async () => {
				try {
					const response = await fetch(`${psServer}/ui/questionnaire`);
					const data = await response.json();
					if (data && data.length > 0) {
						setQuestionnaireFields(data);
					}
				} catch (error) {
					console.error(error);
				} finally {
					setIsLoadingQuestionnaire(false);
				}
			};
			fetchQuestionnaire();
		} else {
			setQuestionnaireFields([]);
			setAnswers({});
			setTermsAccepted(false);
			setIsLoadingQuestionnaire(true);
		}
	}, [showTermsPopup, psServer]);

	const handleAccept = async () => {
		if (!termsAccepted) return;
		try {
			const response = await fetch(`${psServer}/ui/customer/${submittedCode}/terms`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				}
			});
			const data = await response.json();
			if (data === "Success") {
				setShowTermsPopup(false);
			}
		} catch (error) {
			console.error('Error submitting data:', error);
		}
	};

	const handleQuestionnaireSubmit = async () => {
		if (!termsAccepted) return;
		
		const formattedAnswers = Object.values(answers);
		
		try {
			const response = await fetch(`${psServer}/ui/customer/${submittedCode}/questionnaire`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ answers: formattedAnswers })
			});
			const data = await response.json();
			if (response.ok) {
				setShowTermsPopup(false);
			}
		} catch (error) {
			console.error('Error submitting data:', error);
		}
	};
	
	const handleShowTerms = () => {
		window.open('/terms', '_blank', 'noopener,noreferrer');
	};

	const handleAnswerChange = (fieldId, value, questionTitle) => {
		setAnswers(prev => ({
			...prev,
			[fieldId]: {
				question: questionTitle,
				answer: value
			}
		}));
	};

	return (
		<>
			{showTermsPopup && !isLoadingQuestionnaire && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.5, ease: "easeInOut" }}
					className="termsOverlayWrap"
				>
					<div 
						className={`termsOverlay ${questionnaireFields.length > 0 ? 'questionnaireActive' : ''}`} 
						style={{ maxHeight: '90vh', overflowY: 'auto' }}
					>
						<h3>{t('termsOverlayTitle')}</h3>
						
						{questionnaireFields.length > 0 ? (
							<div className="questionnaireContainer" style={{ marginTop: '1.5rem', textAlign: 'left' }}>
								{questionnaireFields.map((field) => (
									<div key={field.id} className="questionnaireField" style={{ marginBottom: '1.25rem' }}>
										<label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
											{field.title}
										</label>
										
										{field.field_type === 'text' && (
											<input 
												type="text" 
												style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #cbd5e1' }}
												value={answers[field.id]?.answer || ''} 
												onChange={(e) => handleAnswerChange(field.id, e.target.value, field.title)} 
											/>
										)}
										
										{field.field_type === 'checkbox' && (
											<label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
												<input 
													type="checkbox" 
													style={{ width: '1.25rem', height: '1.25rem' }}
													checked={answers[field.id]?.answer === 'Yes'} 
													onChange={(e) => handleAnswerChange(field.id, e.target.checked ? 'Yes' : 'No', field.title)} 
												/>
												<span style={{ fontSize: '0.9rem' }}>Yes</span>
											</label>
										)}
										
										{field.field_type === 'dropdown' && (
											<select 
												style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #cbd5e1' }}
												value={answers[field.id]?.answer || ''} 
												onChange={(e) => handleAnswerChange(field.id, e.target.value, field.title)}
											>
												<option value="">-- Select --</option>
												{field.field_options?.map((opt, i) => (
													<option key={i} value={opt}>{opt}</option>
												))}
											</select>
										)}
									</div>
								))}
								
								<div className="questionnaireTermsCheck" style={{ marginTop: '2rem', padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
									<label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', textAlign: 'left' }}>
										<input 
											type="checkbox" 
											style={{ width: '1.25rem', height: '1.25rem', marginTop: '0.1rem', flexShrink: 0 }}
											checked={termsAccepted}
											onChange={(e) => setTermsAccepted(e.target.checked)}
										/>
										<span style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
											{t('termsOverlayMessage1a')} <span className="showTerms" onClick={(e) => { e.preventDefault(); handleShowTerms(); }} style={{ color: '#2563eb', textDecoration: 'underline' }}>{t('termsOverlayMessage1b')}</span>
										</span>
									</label>
								</div>
								
								<button 
									onClick={handleQuestionnaireSubmit} 
									disabled={!termsAccepted}
									style={{ width: '100%', marginTop: '1.5rem', opacity: termsAccepted ? 1 : 0.5, cursor: termsAccepted ? 'pointer' : 'not-allowed' }}
								>
									{t('buttonContinue')}
								</button>
							</div>
						) : (
							<>
								<div style={{ margin: '1rem 0', textAlign: 'left' }}>
									<label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
										<input 
											type="checkbox" 
											style={{ width: '1.25rem', height: '1.25rem', marginTop: '0.1rem', flexShrink: 0 }}
											checked={termsAccepted}
											onChange={(e) => setTermsAccepted(e.target.checked)}
										/>
										<span style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
											{t('termsOverlayMessage1a')} <span className="showTerms" onClick={(e) => { e.preventDefault(); handleShowTerms(); }} style={{ color: '#2563eb', textDecoration: 'underline' }}>{t('termsOverlayMessage1b')}</span>
										</span>
									</label>
								</div>
								<button 
									onClick={handleAccept} 
									disabled={!termsAccepted}
									style={{ width: '100%', opacity: termsAccepted ? 1 : 0.5, cursor: termsAccepted ? 'pointer' : 'not-allowed' }}
								>
									{t('buttonContinue')}
								</button>
							</>
						)}
					</div>
				</motion.div>
			)}
		</>
	);
}

export default TermsOverlay;